import { useChatManagerStore } from '@/lib/store/use-chat-manager-store';
import * as React from 'react'
import { fetchTemplateSelectedAction } from './use-prompt-template-run-on-app-load';
import { usePathname } from 'next/navigation';
import { useAccountMatterStore } from '@/lib/store/use-account-matter-store';
import { useNavigateWithQuery } from '../use-navigate-with-query';
import { usePromptTemplatesStore } from '@/lib/store/use-prompt-templates-store';
import { UrlParams } from '@/constants/url-params';
import { useSidebarStore } from '@/lib/store/use-sidebar-store';
import { nanoid } from '@/lib/utils';
import { isArchieHome } from '@/lib/utils/path-utils';


interface UseSelectPromptTemplateProps {
  handleOpenSelectFilesModal: () => Promise<void>
}

export function useSelectPromptTemplate({
  handleOpenSelectFilesModal,
}: UseSelectPromptTemplateProps) {
  const pathname = usePathname()
  const navigateWithQuery = useNavigateWithQuery()
  const accountId = useAccountMatterStore((store) => store.accountId)
  const matterId = useAccountMatterStore((store) => store.matterId)
  const fileIds = useChatManagerStore((store) => store.fileIds);
  const setFileIds = useChatManagerStore((store) => store.setFileIds);
  const setRunPromptTemplate = useChatManagerStore((store) => store.setRunPromptTemplate);
  const promptTemplateSelected = usePromptTemplatesStore((store) => store.promptTemplateSelected);
  const setPromptTemplateSelected = usePromptTemplatesStore((store) => store.setPromptTemplateSelected);
  const setIsLoadingRunTemplate = usePromptTemplatesStore((store) => store.setIsLoadingRunTemplate);
  const setSelectedChatTab = useSidebarStore((store) => store.setSelectedChatTab);
  const setChatId = useChatManagerStore((store) => store.setChatId);
  const setSelectedSidebarSection = useSidebarStore((store) => store.setSelectedSidebarSection)


  const navNewTemplateChat = React.useCallback((templateId: string, requestDocument: boolean) => {
    const updateState = async () => {
      setIsLoadingRunTemplate(true)
      setSelectedChatTab('chats')
      setSelectedSidebarSection('chats')
      setChatId(nanoid())
      usePromptTemplatesStore.getState().setPromptTemplateSelected(null)
      useChatManagerStore.getState().setRunPromptTemplate(null)
      if (usePromptTemplatesStore.getState().initialTemplateId === templateId) {
        usePromptTemplatesStore.getState().setForceRefresh()
      }
      usePromptTemplatesStore.getState().setInitialTemplateId(null)
    }
    
    const navigate = () => {
      const isAdminChat = pathname.includes('/admin/admin-chat')
      const isHomePage = isArchieHome(pathname)
      
      // Construct base path based on context
      let basePath = '/'
      if (isAdminChat) {
        basePath = '/admin/admin-chat'
      } else if (isHomePage) {
        basePath = '/home'
      }
      
      // Construct query parameters, only include matterId when it's defined (not undefined)
      const queryParams = new URLSearchParams()
      queryParams.set('templateId', templateId)
      
      if (matterId && !isHomePage) {
        queryParams.set('matterId', matterId)
      }
      
      if (isAdminChat && accountId) {
        queryParams.set('accountId', accountId)
      }
      
      if (requestDocument && fileIds.length) {
        queryParams.set('fileIds', fileIds.join(','))
      }
      
      const newPath = `${basePath}?${queryParams.toString()}`
      
      // Strip existing chatId and other unwanted params by whitelisting only the ones we want
      const allowedParams = ['matterId', 'templateId']
      if (isAdminChat) allowedParams.push('accountId')
      if (requestDocument && fileIds.length) allowedParams.push('fileIds')
      
      // use full reload to allow any state already set (such as initialTemplateId) to retrigger useEffects
      if (requestDocument && new URLSearchParams(window.location.search).get(UrlParams.Action) === UrlParams.RunTemplate) {
        // Needs to be refactored so hooks don't require reloading entire page
        window.location.href = newPath
      } else {
        navigateWithQuery(newPath, { 
          refresh: false, 
          existingQueryParamsWhitelist: allowedParams // This strips chatId and other unwanted params
        })
      }
    }
    
    updateState().then(navigate)
  }, [accountId, fileIds, matterId, navigateWithQuery, setIsLoadingRunTemplate, pathname, setChatId, setSelectedChatTab]);

  React.useEffect(() => {
    if (
      promptTemplateSelected?.id &&
      promptTemplateSelected?.requestDocument &&
      fileIds.length
    ) {
      navNewTemplateChat(promptTemplateSelected.id, true)
    }
  }, [promptTemplateSelected?.id, promptTemplateSelected?.requestDocument, fileIds, navNewTemplateChat])

  const handleSelectPromptTemplate = (id: string) => async () => {
    const result = await fetchTemplateSelectedAction(id)
    if (!result) {
      return
    }
    const { requestDocument, disableAutoRun } = result
    if (requestDocument) {
      setPromptTemplateSelected({ id, requestDocument })
      if (!fileIds.length) {
        handleOpenSelectFilesModal()
      }
    } else if (disableAutoRun) {
      setRunPromptTemplate({ id, name: result.templateName || '', disableAutoRun, prompt: result.prompt })
    } else {
      setFileIds([])
      navNewTemplateChat(id, false)
    }
  }

  return {
    handleSelectPromptTemplate,
  }
}
